import React, { createContext, useContext, useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import {
  SpinnerGap,
  SignOut,
  CheckCircle,
  UploadSimple,
  LockSimple,
  House,
  CalendarBlank,
  Certificate,
  WhatsappLogo,
  Key,
  DownloadSimple,
  FileZip,
  Image as ImageIcon,
  GithubLogo,
  Clock as ClockIcon,
  IdentificationCard
} from '@phosphor-icons/react';
import BrandLockup from '../BrandLockup';
import { internPortalAPI } from '../../services/api';

const PortalCtx = createContext(null);
const usePortal = () => useContext(PortalCtx);

const WHATSAPP = '917350695792';

const statusLabel = (status) => {
  if (status === 'completed') return 'Completed';
  if (status === 'submitted') return 'Waiting for approval';
  if (status === 'closed') return 'Account closed';
  return 'In progress';
};

const weekOpen = (intern, task) => (typeof task.is_open === 'boolean' ? task.is_open : Number(intern.current_week || 1) >= Number(task.week_number));

const weekBadge = (intern, task) => {
  if (task.review_status === 'reviewed') {
    return { label: 'Approved', className: 'bg-emerald-50 text-emerald-700' };
  }
  if (task.status === 'submitted') {
    return { label: 'Submitted · waiting review', className: 'bg-sky-50 text-sky-700' };
  }
  if (!weekOpen(intern, task)) {
    return { label: 'Not open yet', className: 'bg-gray-100 text-gray-500' };
  }
  return { label: 'Pending upload', className: 'bg-amber-50 text-amber-800' };
};

const LiveClock = () => {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const day = now.toLocaleDateString('en-IN', { weekday: 'long', timeZone: 'Asia/Kolkata' });
  const date = now.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' });
  const time = now.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Asia/Kolkata' });
  return (
    <div className="mx-3 mb-2 rounded-2xl bg-slate-50 border border-gray-100 px-3 py-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 inline-flex items-center gap-1">
        <ClockIcon className="w-3.5 h-3.5" /> Today
      </p>
      <p className="text-sm font-bold text-gray-900">{day}</p>
      <p className="text-xs text-gray-500">{date}</p>
      <p className="text-sm font-semibold text-brand-700 tabular-nums mt-0.5">{time}</p>
    </div>
  );
};

const helpHref = (intern) => {
  const text = `Hi Growmore, I have a query about my internship.\nName: ${intern.name}\nIntern ID: ${intern.intern_code}\nRole: ${intern.role_title}`;
  return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(text)}`;
};

const InternPortal = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data: res } = await internPortalAPI.me();
      setData(res);
    } catch (err) {
      if (err.response?.data?.account_closed) {
        setError(err.response.data.message);
        localStorage.removeItem('intern_token');
        return;
      }
      setError(err.response?.data?.message || 'Session expired. Please login again.');
      localStorage.removeItem('intern_token');
      navigate('/intern/login');
    }
  };

  useEffect(() => {
    document.title = 'Intern portal | Growmore IT Services';
    if (!localStorage.getItem('intern_token')) {
      navigate('/intern/login');
      return;
    }
    load();
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem('intern_token');
    navigate('/intern/login');
  };

  if (error && !data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="max-w-md bg-white rounded-3xl border p-8 text-center space-y-3">
          <p className="font-display text-xl font-bold">Account closed</p>
          <p className="text-sm text-gray-500">{error}</p>
          <Link to="/certificates" className="btn-primary text-white inline-flex">Find your letter</Link>
          <Link to="/intern/login" className="block text-sm font-semibold text-brand-600">Back to login</Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <p className="pt-32 text-center text-gray-500"><SpinnerGap className="w-6 h-6 animate-spin inline" /></p>;
  }

  const intern = data.intern;
  const nav = [
    { to: '/intern/portal', end: true, label: 'Dashboard', icon: House },
    { to: '/intern/portal/info', end: true, label: 'Personal info', icon: IdentificationCard },
    { to: '/intern/portal/weeks', end: false, label: 'Weeks', icon: CalendarBlank },
    { to: '/intern/portal/letters', end: false, label: 'Letters', icon: Certificate }
  ];

  return (
    <PortalCtx.Provider value={{ data, intern, error, setError, busy, setBusy, load, logout }}>
      <div className="min-h-[100dvh] bg-slate-50 flex">
        <aside className="hidden md:flex w-64 shrink-0 flex-col bg-white border-r border-gray-100">
          <div className="px-4 py-5 border-b border-gray-100">
            <BrandLockup size="sm" />
          </div>
          <nav className="p-3 flex-1 space-y-1">
            {nav.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    `w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl text-sm font-semibold ${
                      isActive ? 'bg-brand-600 text-white' : 'text-gray-600 hover:bg-slate-50'
                    }`
                  }
                >
                  <Icon className="w-5 h-5" /> {item.label}
                </NavLink>
              );
            })}
          </nav>
          <LiveClock />
          <div className="px-3 pb-2 space-y-2">
            <a
              href={helpHref(intern)}
              target="_blank"
              rel="noreferrer"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-emerald-700 bg-emerald-50"
            >
              <WhatsappLogo className="w-4 h-4" weight="fill" /> Ask on WhatsApp
            </a>
            <Link to="/intern/forgot" className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-semibold text-gray-600 hover:bg-slate-50">
              <Key className="w-4 h-4" /> Forgot password
            </Link>
          </div>
          <button type="button" onClick={logout} className="m-3 flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-500">
            <SignOut className="w-4 h-4" /> Logout
          </button>
        </aside>

        <div className="flex-1 min-w-0">
          <header className="bg-white/95 backdrop-blur border-b border-gray-100 sticky top-0 z-20 pt-[env(safe-area-inset-top)]">
            <div className="px-3 md:px-4 h-12 md:h-auto md:py-3 flex items-center justify-between gap-3">
              <div className="md:hidden min-w-0"><BrandLockup size="sm" /></div>
              <p className="hidden md:block text-sm font-semibold text-gray-700 truncate">{intern.name}</p>
              <button type="button" onClick={logout} className="md:hidden inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-red-600 text-sm font-semibold" aria-label="Logout">
                <SignOut className="w-5 h-5" />
                <span>Logout</span>
              </button>
            </div>
          </header>
          <main className="px-3 md:px-6 py-3 md:py-6 pb-24 md:pb-6 w-full">
            {error && <p className="mb-4 text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">{error}</p>}
            <Routes>
              <Route index element={<Dashboard />} />
              <Route path="info" element={<PersonalInfo />} />
              <Route path="weeks" element={<WeeksList />} />
              <Route path="weeks/:week" element={<WeekDetail />} />
              <Route path="letters" element={<Letters />} />
              <Route path="*" element={<Navigate to="/intern/portal" replace />} />
            </Routes>
          </main>
          <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-gray-100 app-bottom-nav">
            <div className="grid grid-cols-4">
              {nav.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={({ isActive }) =>
                      `flex flex-col items-center justify-center gap-0.5 py-2 text-[10px] font-semibold ${
                        isActive ? 'text-brand-600' : 'text-gray-400'
                      }`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <Icon className="w-5 h-5" weight={isActive ? 'fill' : 'regular'} />
                        {item.label === 'Personal info' ? 'Info' : item.label}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </nav>
        </div>
      </div>
    </PortalCtx.Provider>
  );
};

const Dashboard = () => {
  const { intern, data } = usePortal();
  const total = intern.weeks_total || 0;
  const submitted = intern.weeks_submitted || 0;
  const pending = intern.weeks_pending ?? Math.max(0, total - submitted);
  const accepted = intern.weeks_reviewed || 0;
  const tasks = data.tasks || [];

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">Dashboard</p>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-gray-900">Your internship</h1>
        <p className="text-sm text-gray-500 mt-1">Week {intern.current_week} of {total} is open now. The next week opens Monday at 9:00 AM IST.</p>
      </div>

      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          ['Total weeks', total, 'bg-white'],
          ['Submitted', submitted, 'bg-sky-50'],
          ['Pending', pending, 'bg-amber-50'],
          ['Admin accepted', accepted, 'bg-emerald-50']
        ].map(([label, value, bg]) => (
          <div key={label} className={`rounded-2xl border border-gray-100 p-4 ${bg}`}>
            <p className="text-xs font-semibold uppercase text-gray-400">{label}</p>
            <p className="mt-1 font-display text-3xl font-extrabold text-gray-900">{value}</p>
          </div>
        ))}
      </section>

      <section className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-5 space-y-3">
        <div className="flex flex-wrap justify-between gap-2">
          <h2 className="font-display font-bold">Weeks</h2>
          <Link to="/intern/portal/weeks" className="text-sm font-semibold text-brand-600">Open all weeks</Link>
        </div>
        <p className="text-sm text-gray-500">Each week has its own task. Green tick = submitted. Open a week to upload.</p>
        <div className="flex flex-wrap gap-2">
          {tasks.slice(0, 12).map((task) => {
            const badge = weekBadge(intern, task);
            return (
              <Link
                key={task.id}
                to={`/intern/portal/weeks/${task.week_number}`}
                className={`px-3 py-2 rounded-xl text-sm font-semibold border inline-flex items-center gap-1.5 ${
                  task.review_status === 'reviewed'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
                    : task.status === 'submitted'
                      ? 'bg-sky-50 text-sky-700 border-sky-100'
                      : 'bg-slate-50 text-gray-600 border-gray-100'
                }`}
              >
                {task.review_status === 'reviewed' || task.status === 'submitted' ? <CheckCircle className="w-4 h-4" weight="fill" /> : <span className="w-4 h-4 rounded-full border-2 border-gray-300" />}
                Week {task.week_number}
                <span className="hidden sm:inline text-[10px] font-semibold opacity-80">· {badge.label}</span>
              </Link>
            );
          })}
          {tasks.length > 12 && (
            <Link to="/intern/portal/weeks" className="px-3 py-2 rounded-xl text-sm font-semibold border border-gray-100 text-gray-500">
              +{tasks.length - 12} more
            </Link>
          )}
        </div>
      </section>

      <section className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-5 space-y-3">
        <h2 className="font-display font-bold">How weekly work works</h2>
        <ul className="text-sm text-gray-600 leading-6 space-y-2 list-disc pl-5">
          <li>Week 1 opens on your join date at 9:00 AM IST. Every later week opens on Monday at 9:00 AM IST.</li>
          <li>If that Monday has passed, you can still upload. Late submit is allowed — the task does not expire.</li>
          <li>Upload your project code as a <strong>ZIP file</strong> (do not include node_modules or vendor).</li>
          <li>If you pushed the work to GitHub or another host, add that link. The link is optional.</li>
          <li>Upload a clear <strong>output screenshot</strong> of what you built or ran.</li>
          <li>Write what you did that week in your own words.</li>
          <li>After you submit, admin review shows on the Weeks page. Approved means that week is accepted.</li>
        </ul>
      </section>

      <section className="grid sm:grid-cols-2 gap-3">
        <a
          href={helpHref(intern)}
          target="_blank"
          rel="noreferrer"
          className="rounded-2xl sm:rounded-3xl bg-emerald-50 border border-emerald-100 p-4 sm:p-5 flex items-start gap-3"
        >
          <WhatsappLogo className="w-8 h-8 text-emerald-600 shrink-0" weight="fill" />
          <div>
            <p className="font-display font-bold text-gray-900">Ask freely on WhatsApp</p>
            <p className="text-sm text-gray-600 mt-1">Any internship question — send it on WhatsApp 7350695792. We will help.</p>
            <p className="text-sm font-semibold text-emerald-700 mt-2">Open WhatsApp →</p>
          </div>
        </a>
        <Link to="/intern/forgot" className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-5 flex items-start gap-3">
          <Key className="w-8 h-8 text-brand-600 shrink-0" />
          <div>
            <p className="font-display font-bold text-gray-900">Forgot password</p>
            <p className="text-sm text-gray-600 mt-1">Change it with your current password, or get a one-time code from info@growmoreitservices.com.</p>
            <p className="text-sm font-semibold text-brand-600 mt-2">Reset password →</p>
          </div>
        </Link>
      </section>
    </div>
  );
};

const PersonalInfo = () => {
  const { intern } = usePortal();
  const total = intern.weeks_total || 0;
  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">Personal info</p>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-gray-900">{intern.name}</h1>
        <p className="text-sm text-gray-500 mt-1">{intern.email} · {intern.phone}</p>
      </div>
      <section className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-5 shadow-soft">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {[
            ['Internship', intern.role_title],
            ['Intern ID', intern.intern_code],
            ['College', intern.college],
            ['Date of birth', intern.date_of_birth || '—'],
            ['Join date', intern.started_at || '—'],
            ['Complete date', intern.completed_at || '—'],
            ['Duration', `${total} weeks${intern.plan_name ? ` · ${intern.plan_name}` : ''}`],
            ['Progress', `${intern.weeks_submitted || 0} / ${total} weeks submitted`],
            ['Admin accepted', `${intern.weeks_reviewed || 0} weeks`],
            ['Status', statusLabel(intern.status)]
          ].map(([label, value]) => (
            <div key={label} className="rounded-2xl bg-slate-50 px-3 py-2.5">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
              <p className="mt-0.5 font-semibold text-gray-900 break-all">{value || '—'}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

const WeeksList = () => {
  const { data, intern } = usePortal();
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">Weeks</p>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-gray-900">{intern.weeks_total}-week internship</h1>
        <p className="text-sm text-gray-500 mt-1">
          {intern.weeks_submitted || 0} submitted · {intern.weeks_reviewed || 0} accepted by admin · {intern.weeks_pending ?? 0} pending
        </p>
      </div>
      <div className="space-y-3">
        {(data.tasks || []).map((task) => {
          const badge = weekBadge(intern, task);
          const open = weekOpen(intern, task);
          return (
            <Link key={task.id} to={`/intern/portal/weeks/${task.week_number}`} className="block rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-5 hover:border-brand-200">
              <div className="flex justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold uppercase text-gray-400">Week {task.week_number}</p>
                  <h2 className="font-display font-bold text-gray-900">{task.title}</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {open ? `Open since ${task.opens_label || 'join'}` : `Opens ${task.opens_label || 'Monday, 9:00 AM IST'}`}
                  </p>
                  <p className="text-sm text-gray-500 mt-1 line-clamp-2">{task.description}</p>
                </div>
                <span className={`shrink-0 inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full h-fit ${badge.className}`}>
                  {task.review_status === 'reviewed' || task.status === 'submitted' ? <CheckCircle className="w-4 h-4" weight="fill" /> : !open ? <LockSimple className="w-4 h-4" /> : null}
                  {badge.label}
                </span>
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

const WeekDetail = () => {
  const { week } = useParams();
  const { data, intern, busy, setBusy, setError, load } = usePortal();
  const [text, setText] = useState('');
  const [repo, setRepo] = useState('');
  const [zip, setZip] = useState(null);
  const [shot, setShot] = useState(null);
  const task = (data.tasks || []).find((item) => String(item.week_number) === String(week));
  if (!task) return <Navigate to="/intern/portal/weeks" replace />;
  const open = weekOpen(intern, task);
  const done = task.status === 'submitted';
  const reviewed = task.review_status === 'reviewed';
  const shots = task.screenshot_urls || [];

  const submit = async () => {
    if (!text.trim()) {
      setError('Write what you did this week.');
      return;
    }
    if (!zip) {
      setError('Upload your project as a ZIP file.');
      return;
    }
    if (!shot) {
      setError('Upload an output screenshot.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('submission_text', text);
      if (repo.trim()) form.append('repo_url', repo.trim());
      form.append('zip', zip);
      form.append('screenshot', shot);
      await internPortalAPI.submitTask(task.id, form);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Could not submit week.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <Link to="/intern/portal/weeks" className="text-sm font-semibold text-brand-600">← All weeks</Link>
      <div className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-6 space-y-4">
        <div className="flex flex-wrap justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase text-gray-400">Week {task.week_number}</p>
            <h1 className="font-display text-2xl font-extrabold text-gray-900">{task.title}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {open ? `Open since ${task.opens_label}` : `Opens ${task.opens_label}`}
            </p>
          </div>
          {done && (
            <span className={`inline-flex items-center gap-1 text-sm font-semibold ${reviewed ? 'text-emerald-700' : 'text-sky-700'}`}>
              <CheckCircle className="w-5 h-5" weight="fill" /> {reviewed ? 'Approved by admin' : 'Submitted · waiting review'}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-600 leading-6 whitespace-pre-wrap">{task.description}</p>
        <ul className="text-sm text-gray-500 list-disc pl-5 space-y-1">
          {(task.deliverables || []).map((item) => <li key={item}>{item}</li>)}
        </ul>

        {done ? (
          <div className="rounded-2xl bg-slate-50 border border-gray-100 p-4 space-y-3">
            <p className="text-xs font-semibold uppercase text-gray-400">What you submitted</p>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{task.submission_text || '—'}</p>
            {task.repo_url && (
              <a href={task.repo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-600">
                <GithubLogo className="w-4 h-4" /> {task.repo_url}
              </a>
            )}
            <div className="flex flex-wrap gap-3">
              {task.zip_url && (
                <a href={task.zip_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white border text-sm font-semibold">
                  <FileZip className="w-4 h-4" /> Download project ZIP
                </a>
              )}
            </div>
            {shots.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-3">
                {shots.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer" className="block rounded-2xl overflow-hidden border bg-white">
                    <img src={url} alt="Output screenshot" className="w-full h-48 object-contain bg-slate-50" />
                    <p className="text-xs font-semibold text-brand-600 px-3 py-2">Open screenshot</p>
                  </a>
                ))}
              </div>
            )}
            {!task.zip_url && !shots.length && (task.submission_urls || []).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {task.submission_urls.map((url) => (
                  <a key={url} href={url} target="_blank" rel="noreferrer" className="text-sm font-semibold text-brand-600">Open file</a>
                ))}
              </div>
            )}
          </div>
        ) : open ? (
          <div className="space-y-3 rounded-2xl border border-gray-100 p-4">
            <p className="text-sm font-semibold text-gray-800">Upload this week</p>
            <label className="block text-xs font-semibold text-gray-500">
              What you did this week
              <textarea rows={5} className="input-field mt-1 text-sm font-normal" placeholder="Write the work in your own words..." value={text} onChange={(e) => setText(e.target.value)} />
            </label>
            <label className="block text-xs font-semibold text-gray-500">
              Project ZIP <span className="text-red-500">required</span>
              <span className="block font-normal text-gray-400 mt-0.5">Code only. No node_modules / vendor. Max 20 MB.</span>
              <input
                type="file"
                accept=".zip,application/zip"
                className="mt-1 block w-full text-sm"
                onChange={(e) => setZip(e.target.files?.[0] || null)}
              />
              {zip && <p className="mt-1 text-xs text-gray-600 inline-flex items-center gap-1"><FileZip className="w-3.5 h-3.5" /> {zip.name}</p>}
            </label>
            <label className="block text-xs font-semibold text-gray-500">
              Output screenshot <span className="text-red-500">required</span>
              <span className="block font-normal text-gray-400 mt-0.5">JPG, PNG or WebP. Show the running result.</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                className="mt-1 block w-full text-sm"
                onChange={(e) => setShot(e.target.files?.[0] || null)}
              />
              {shot && <p className="mt-1 text-xs text-gray-600 inline-flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> {shot.name}</p>}
            </label>
            <label className="block text-xs font-semibold text-gray-500">
              GitHub / repo link <span className="font-normal text-gray-400">optional</span>
              <input
                className="input-field mt-1 text-sm font-normal"
                placeholder="https://github.com/you/project"
                value={repo}
                onChange={(e) => setRepo(e.target.value)}
              />
            </label>
            <button type="button" disabled={busy} onClick={submit} className="btn-primary text-white inline-flex items-center gap-2">
              {busy ? <SpinnerGap className="w-4 h-4 animate-spin" /> : <UploadSimple className="w-4 h-4" />}
              Submit week {task.week_number}
            </button>
          </div>
        ) : (
          <div className="text-sm text-gray-600 rounded-2xl bg-slate-50 border border-gray-100 px-4 py-4 space-y-1">
            <p className="inline-flex items-start gap-2 font-semibold text-gray-800">
              <LockSimple className="w-4 h-4 mt-0.5 shrink-0" />
              Opens {task.opens_label || 'Monday, 9:00 AM IST'}
            </p>
            <p>Each new week opens on Monday at 9:00 AM IST. After that date you can upload even if you are late.</p>
          </div>
        )}
      </div>
    </div>
  );
};

const publicId = (code) => String(code || '').replace(/GMITS-/gi, 'GMIS-');

const Letters = () => {
  const { data } = usePortal();
  const letters = data.letters || [];
  const offer = letters.find((item) => item.type === 'offer');
  const completion = letters.find((item) => item.type === 'completion');
  const unlocked = Boolean(completion);

  return (
    <div className="space-y-5">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-600">Letters</p>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-gray-900">Official letters</h1>
        <p className="text-sm text-gray-500 mt-1">Offer letter is available after enrolment. Completion letter appears here after admin issues it. Print or save as PDF from the letter page.</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <article className="rounded-2xl sm:rounded-3xl bg-white border border-gray-100 p-4 sm:p-6 flex flex-col min-h-[220px] sm:min-h-[260px]">
          <p className="text-xs font-bold uppercase text-brand-600">Offer letter</p>
          <h2 className="font-display text-2xl font-extrabold text-gray-900 mt-1">Internship offer</h2>
          {offer ? (
            <>
              <p className="text-sm text-gray-500 mt-2">ID {publicId(offer.letter_code)}</p>
              <p className="text-sm text-gray-600 mt-3 flex-1">This is your official offer. Open it to view, then print or save as PDF.</p>
              <div className="flex flex-wrap gap-2 mt-4">
                <Link to={`/verify/${offer.letter_code}`} className="btn-primary text-white inline-flex items-center gap-2 text-sm">
                  <DownloadSimple className="w-4 h-4" /> Download / print
                </Link>
              </div>
            </>
          ) : (
            <p className="text-sm text-gray-500 mt-4">Issued after enrolment. Check again if you just paid.</p>
          )}
        </article>

        <article className={`rounded-2xl sm:rounded-3xl border p-4 sm:p-6 flex flex-col min-h-[220px] sm:min-h-[260px] ${unlocked ? 'bg-white border-gray-100' : 'bg-slate-50 border-gray-200'}`}>
          <p className="text-xs font-bold uppercase text-brand-600">Completion letter</p>
          <h2 className="font-display text-2xl font-extrabold text-gray-900 mt-1">Internship complete</h2>
          {unlocked ? (
            <>
              <p className="text-sm text-gray-500 mt-2">ID {publicId(completion.letter_code)}</p>
              <Link to={`/verify/${completion.letter_code}`} className="btn-primary text-white inline-flex items-center gap-2 text-sm mt-4 w-fit">
                <DownloadSimple className="w-4 h-4" /> Download / print
              </Link>
            </>
          ) : (
            <>
              <div className="mt-6 flex-1 flex flex-col items-start gap-3">
                <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-600 bg-white border border-gray-200 rounded-full px-3 py-1.5">
                  <LockSimple className="w-4 h-4" weight="fill" /> Locked
                </span>
                <p className="text-sm text-gray-500 leading-6">
                  This letter stays locked until admin issues it from the last-week Approve → Final → Issue flow.
                </p>
              </div>
            </>
          )}
        </article>
      </div>
    </div>
  );
};

export default InternPortal;
